import React from "react";
import { ArrowUpRight, Star } from "lucide-react";
import { caseDecks } from "../data/mock";

const STAR_STYLES = {
  silver: {
    gradient: "linear-gradient(135deg, #F4F4F5 0%, #D4D4D8 45%, #8E8E93 100%)",
    shine: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9), transparent 45%)",
    label: "Silver"
  },
  gold: {
    gradient: "linear-gradient(135deg, #FFF3B0 0%, #FFD700 45%, #B8860B 100%)",
    shine: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9), transparent 45%)",
    label: "Gold"
  },
  diamond: {
    gradient: "linear-gradient(135deg, #E0F7FA 0%, #7DD3FC 45%, #0EA5E9 100%)",
    shine: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95), transparent 45%)",
    label: "Diamond"
  }
};

function MedalBadge({ star }) {
  const s = STAR_STYLES[star];
  if (!s) return null;
  return (
    <div className="relative w-11 h-11 shrink-0">
      <div
        className="w-full h-full rounded-full border-[1.5px] border-[#0F0F10] shadow-[2px_2px_0_#0F0F10] grid place-items-center"
        style={{ background: s.gradient }}
      >
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{ background: s.shine, mixBlendMode: "screen" }}
        />
        <Star
          className="w-5 h-5 relative"
          fill="#0F0F10"
          stroke="#0F0F10"
          strokeWidth={1.2}
        />
      </div>
    </div>
  );
}

function DeckThumb({ deck }) {
  const isLight = deck.color.startsWith("#F") || deck.color.startsWith("#D");
  return (
    <div
      className="relative aspect-[16/10] rounded-2xl overflow-hidden border-[1.5px] border-[#0F0F10] flex flex-col justify-center px-6 py-5"
      style={{ background: deck.color }}
    >
      {deck.id === 2 && (
        <div className="absolute left-0 top-0 bottom-0 w-3 bg-[#FCD34D]"></div>
      )}
      {deck.id === 3 && (
        <div
          className="absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(circle at 70% 30%, rgba(255,255,255,0.35), transparent 50%)"
          }}
        ></div>
      )}
      <div
        className="font-bold text-2xl leading-tight"
        style={{ color: deck.accentColor }}
      >
        {deck.badge}
      </div>
      {deck.subtitle ? (
        <div
          className="mt-2 text-sm font-medium"
          style={{ color: isLight ? "#0F0F10" : "#FFFFFF" }}
        >
          {deck.subtitle}
        </div>
      ) : null}
      {deck.college ? (
        <div
          className="mt-3 text-xs font-mono opacity-70"
          style={{ color: isLight ? "#0F0F10" : "#FFFFFF" }}
        >
          {deck.college}
        </div>
      ) : null}
    </div>
  );
}

export default function CaseDecksSection() {
  return (
    <section id="cases" className="py-24 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0F0F10] text-[#FFE357] text-xs font-semibold uppercase tracking-widest mb-4">
              ★ Competitions
            </div>
            <h2 className="text-5xl sm:text-6xl font-extrabold tracking-tight">
              Winning <span className="squiggle">Case Decks</span>
            </h2>
            <p className="mt-4 text-lg text-[#0F0F10]/70 max-w-2xl">
              Strategy &amp; product decks that placed in case competitions across India — covering everything from supply-side growth to fintech super-apps.
            </p>
          </div>
          <div className="font-mono text-sm text-[#0F0F10]/60">winners × 6</div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {caseDecks.map((d) => (
            <article key={d.id} className="card-soft p-5 flex flex-col">
              <DeckThumb deck={d} />
              <h3 className="mt-5 text-lg font-bold leading-snug min-h-[3.5rem]">
                {d.title}
              </h3>

              {d.star ? (
                <div className="mt-2 flex items-center gap-3">
                  <MedalBadge star={d.star} />
                  <div>
                    <div className="text-sm font-bold leading-tight">
                      {d.award}
                    </div>
                    <div className="text-xs text-[#0F0F10]/65 font-mono">
                      {d.stat}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-sm text-[#0F0F10]/65">{d.subtitle}</p>
              )}

              <div className="mt-5">
                {d.url ? (
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-yellow text-sm py-2 px-5"
                  >
                    Know More
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                ) : (
                  <button className="btn-yellow text-sm py-2 px-5">
                    Know More
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
